import { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@watershed/ui-core/components/Tooltip';
import Typography from '@mui/material/Typography';

import { Flags } from '@watershed/constants/flags';
import DeleteIcon from '@watershed/icons/components/Delete';
import LockKeyhole from '@watershed/icons/components/LockKeyhole';
import SearchIcon from '@watershed/icons/components/Search';
import UnlockKeyhole from '@watershed/icons/components/UnlockKeyhole';
import OverflowChipList from '@watershed/shared-frontend/components/OverflowChipList';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import BlankSlate from '@watershed/ui-core/components/BlankSlate';
import {
  GridColDef,
  GridToolbarQuickFilter,
} from '@watershed/ui-core/components/DataGrid/DataGrid';
import DatePill from '@watershed/ui-core/components/DatePill';
import { useDialogConfirm } from '@watershed/ui-core/components/DialogConfirm';
import { ObjectList } from '@watershed/ui-core/components/ObjectList/ObjectList';
import { getOverflowMenuColumn } from '@watershed/ui-core/components/ObjectList/ObjectListOverflowMenu';

import type { GQApiKeyForApiKeyPageFragment } from '../../../generated/graphql-operations';
import { useDeleteApiKeyMutation } from '../../../generated/urql';
import { useFeatureFlag } from '../../../utils/FeatureFlag';
import { objectListContainerStyles, pageDataGridStyles } from '../utils/styles';
import { AuthMethodDropdown } from './AuthMethodDropdown';

function Toolbar({
  selectedAuthMethod,
  setSelectedAuthMethod,
  hasApiKeys,
}: {
  selectedAuthMethod: 'token' | 'oauth';
  setSelectedAuthMethod: (method: 'token' | 'oauth') => void;
  hasApiKeys: boolean;
}) {
  const { t } = useLingui();
  const isOAuthEnabled = useFeatureFlag(Flags.EntFoundEnableOAuth);
  return (
    <Stack direction="row" component="header" justifyContent="space-between">
      <GridToolbarQuickFilter
        sx={{ flexGrow: 0.15, minWidth: 256 }}
        placeholder={t({
          message: 'Search API keys…',
          context: 'Placeholder for the API keys search bar',
        })}
      />
      {isOAuthEnabled && (
        <AuthMethodDropdown
          selectedAuthMethod={selectedAuthMethod}
          setSelectedAuthMethod={setSelectedAuthMethod}
          hasOAuthClients={false}
          hasApiKeys={hasApiKeys}
        />
      )}
    </Stack>
  );
}

export default function ApiKeyGrid({
  apiKeys,
  selectedAuthMethod,
  setSelectedAuthMethod,
}: {
  apiKeys: Array<GQApiKeyForApiKeyPageFragment>;
  selectedAuthMethod: 'token' | 'oauth';
  setSelectedAuthMethod: (method: 'token' | 'oauth') => void;
}) {
  const { t } = useLingui();
  const { enqueueSnackbar } = useSnackbar();
  const [, executeDeleteApiKey] = useDeleteApiKeyMutation();
  const openConfirm = useDialogConfirm();
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>(
    {}
  );

  const toggleSecretVisibility = useCallback((id: string) => {
    setVisibleSecrets((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleDelete = useCallback(
    async (rowId: string) => {
      const didConfirm = await openConfirm({
        title: 'Delete API key',
        message: 'Are you sure you want to delete this API key?',
        confirmText: 'Delete',
      });
      if (didConfirm) {
        const result = await executeDeleteApiKey({
          input: { id: rowId },
        });
        if (result.error) {
          enqueueSnackbar('Error deleting API key.', {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('API key deleted.', {
            variant: 'success',
          });
        }
      }
    },
    [enqueueSnackbar, executeDeleteApiKey, openConfirm]
  );

  const columns: Array<GridColDef<GQApiKeyForApiKeyPageFragment>> = [
    {
      field: 'name',
      headerName: t({
        message: 'Name',
        context: 'Field label',
      }),
      flex: 1,
      editable: false,
      renderCell: ({ row }) => (
        <ObjectList.TitleCell title={row.name} subtitle={row.description} />
      ),
    },
    {
      field: 'secret',
      headerName: t({
        message: 'Secret',
        context: 'Field label',
      }),
      flex: 0.75,
      editable: false,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Tooltip
            title={
              visibleSecrets[row.id]
                ? row.secret
                : t({
                    message: 'Click the lock icon to reveal',
                    context: 'Tooltip for masked API key',
                  })
            }
          >
            <Typography
              variant="caption"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 40px)',
                color: (theme) => theme.palette.grey50,
                fontFamily: (theme) => theme.typography.fontFamilyMonospace,
              }}
            >
              {visibleSecrets[row.id] ? row.secret : 'wskey_' + '•'.repeat(20)}
            </Typography>
          </Tooltip>
          <IconButton
            aria-label={
              visibleSecrets[row.id]
                ? `Hide secret ${row.id}`
                : `Show secret ${row.id}`
            }
            size="small"
            onClick={() => toggleSecretVisibility(row.id)}
            sx={{
              ml: 'auto',
              border: 0,
              boxShadow: 'none',
              bgcolor: 'transparent',
              padding: '6px',
              '& svg': {
                transform: visibleSecrets[row.id] ? 'scale(1.1)' : 'scale(1)',
              },
              '&:hover': {
                backgroundColor: (theme) => theme.palette.grey20,
                color: 'inherit',
              },
              '&:focus, &:focus-visible': {
                border: 0,
                boxShadow: 'none',
                color: 'inherit',
              },
            }}
          >
            {visibleSecrets[row.id] ? <UnlockKeyhole /> : <LockKeyhole />}
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'roles',
      headerName: t({
        message: 'Roles',
        context: 'Field label',
      }),
      description: t({
        message:
          'The roles which control the permissions that this API key has',
        context: 'Field description',
      }),
      flex: 0.75,
      editable: false,
      renderCell: ({ row }) => (
        <OverflowChipList
          items={row.roles.map((record) => ({
            label: record.name,
          }))}
          itemDisplay={{
            type: 'popover',
            renderPopover: ({ items }) => (
              <Stack
                gap={1}
                flexWrap="wrap"
                sx={{
                  p: 1,
                  width: 320,
                  border: (theme) => `thin solid ${theme.palette.grey20}`,
                  borderRadius: '10px',
                }}
              >
                <Typography variant="body2">
                  {t({
                    message: 'Roles:',
                    context: 'Field label',
                  })}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {items.map((item) => (
                    <Chip key={item.label} label={item.label} />
                  ))}
                </Stack>
              </Stack>
            ),
          }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: t({
        message: 'Created',
        context: 'The date a role was created',
      }),
      flex: 0.6,
      renderCell: ({ row }) => (
        <DatePill
          date={row.createdAt}
          sx={{
            backgroundColor: 'transparent',
          }}
        />
      ),
    },

    getOverflowMenuColumn(({ row }) => ({
      actions: [
        {
          id: 'delete',
          variant: 'error',
          IconElement: DeleteIcon,
          onClick: () => handleDelete(row.id),
          testId: `delete-api-key-${row.id}`,
        },
      ],
    })),
  ];

  return (
    <Box sx={objectListContainerStyles}>
      <ObjectList<GQApiKeyForApiKeyPageFragment>
        sx={pageDataGridStyles}
        id="ApiKeyGrid"
        autoHeight
        pagination={false}
        rows={apiKeys}
        columns={columns}
        disableColumnPinning
        disableAggregation
        slots={{
          noRowsOverlay: () => (
            <BlankSlate
              title={t({
                message: 'No API keys found',
                context: 'Empty state message for API key list',
              })}
              icon={LockKeyhole}
              sx={{
                height: '100%',
                width: '100%',
              }}
            />
          ),
          noResultsOverlay: () => (
            <BlankSlate
              title={
                <Trans context="Empty state title when no API keys are found after searching.">
                  No API keys found
                </Trans>
              }
              subtitle={
                <Trans context="Empty state description when no API keys are found after searching">
                  No API keys match your search criteria.
                </Trans>
              }
              icon={SearchIcon}
              sx={{
                height: '100%',
                width: '100%',
              }}
            />
          ),
          toolbar: Toolbar,
        }}
        slotProps={{
          toolbar: {
            selectedAuthMethod,
            setSelectedAuthMethod,
            hasApiKeys: apiKeys.length > 0,
          },
        }}
      />
    </Box>
  );
}
